/**
 * Map Configuration Constants
 *
 * Centralized configuration for zoom thresholds, clustering behavior,
 * and map styling across the application.
 */

// Zoom level thresholds for different visualization strategies
export const ZOOM_THRESHOLDS = {
  // Zoom 0-15: Small dots (individual, unclustered)
  SMALL_DOTS_MIN: 0,
  SMALL_DOTS_MAX: 15,

  // Zoom 15+: Medium markers with icons
  MEDIUM_MARKERS_MIN: 15,
  MEDIUM_MARKERS_MAX: 17,

  // Zoom 17+: Full-size markers (tooltip-sized)
  FULL_MARKERS_MIN: 17,

  // Maximum auto-zoom for cluster expansion (user can zoom closer manually)
  MAX_AUTO_ZOOM: 14,

  // No hover tooltips at or below this zoom — dots too small for meaningful hover
  NO_HOVER_THRESHOLD: 6,

  // Super zoom: At or below this zoom, clicking a dot zooms to show nearby markers
  SUPER_ZOOM_THRESHOLD: 6,
  SUPER_ZOOM_TARGET: 15, // Zoom level to fly to — reveals medium markers in the area
} as const;

// Clustering configuration — two sources: dots (unclustered) and clusters (clustered at zoom 9+)
export const CLUSTER_CONFIG = {
  dots: {
    enabled: false,
  },
  clusters: {
    enabled: true,
    minZoom: 8,
    maxZoom: 15,
    radius: 25,
    minPoints: 25,
  },
} as const;

// Popup size thresholds for SmallClusterPopup vs ClusterPopup (clusters start at 2+)
export const POPUP_THRESHOLDS = {
  SMALL_CLUSTER_MAX: 10, // 2-10 units: SmallClusterPopup (icon grid)
  LARGE_CLUSTER_MIN: 11, // 11+ units: ClusterPopup (company-grouped list)
} as const;

// Marker sizing and styling
export const MARKER_STYLES = {
  cluster: {
    color: '#8FB560', // Hollywood Trucks green
    strokeWidth: 2,
    strokeColor: '#ffffff',
    opacity: 1.0, // Fully opaque, no transparency
    // Graduated sizes based on point count
    sizes: [
      { maxCount: 9, radius: 23 },   // 2-9 units
      { maxCount: 49, radius: 30 },  // 10-49 units
      { maxCount: Infinity, radius: 38 }, // 50+ units
    ],
  },

  smallDot: {
    strokeWidth: 1,
    strokeColor: '#ffffff',
    opacity: 1.0, // Solid, no transparency
    // Fixed radius — dots stay the same size at all zoom levels until icons appear at zoom 9
    zoomScale: [
      { zoom: 0, radius: 4 },
      { zoom: 9, radius: 4 },
    ],
  },

  // Medium markers (zoom 10-14): with icon
  mediumMarker: {
    radius: 22,
    strokeWidth: 2,
    strokeColor: '#ffffff',
    opacity: 1.0,
    minZoom: 10,
    maxZoom: 14,
  },

  // Large markers (zoom 14+): tooltip-sized (matches SmallClusterContent w-9 = 36px)
  fullMarker: {
    radius: 36,
    strokeWidth: 3,
    strokeColor: '#ffffff',
    opacity: 1.0,
    minZoom: 14,
  },

  hover: {
    color: '#8FB560', // Hollywood Trucks green
    dotRadius: 6, // Small dots hover
    mediumRadius: 13, // Medium marker hover
    fullRadius: 22, // Large marker hover
    opacity: 0.6,
    strokeWidth: 3,
  },
} as const;

// Status color mapping
export const STATUS_COLORS = {
  online: {
    light: '#059669',  // green-600
    dark: '#10b981',   // green-500
  },
  idle: {
    light: '#9ca3af',  // gray-400
    dark: '#9ca3af',   // gray-400
  },
  offline: {
    light: '#dc2626',  // red-600
    dark: '#ef4444',   // red-500
  },
  default: '#9ca3af', // gray-400
} as const;

// Map style URLs
export const MAP_STYLES = {
  dark: 'mapbox://styles/mapbox/dark-v11',
  light: 'mapbox://styles/mapbox/light-v11',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
  standard: 'mapbox://styles/mapbox/standard',
} as const;

// Default map settings - Global view (for fleets with units outside NA/UK)
export const DEFAULT_MAP_CONFIG_GLOBAL = {
  center: [-25.865284, 12.440140] as [number, number], // Atlantic - global view
  zoom: 2, // World overview
  maxZoom: 21,
  projection: { name: 'mercator' as const },
} as const;

// Default map settings - USA view (for fleets with units primarily in USA)
export const DEFAULT_MAP_CONFIG_NA_UK = {
  center: [-98.5795, 39.8283] as [number, number], // Geographic center of continental USA (Kansas)
  zoom: 4, // USA regional overview
  maxZoom: 21,
  projection: { name: 'mercator' as const },
} as const;

// Legacy default (use NA/UK as the primary default)
export const DEFAULT_MAP_CONFIG = DEFAULT_MAP_CONFIG_NA_UK;

// Bounding box for North America and UK region
export const NA_UK_BOUNDS = {
  // North America: roughly -170 to -50 lng, 15 to 85 lat
  // UK/Ireland: roughly -12 to 2 lng, 49 to 61 lat
  isWithinNaUk: (lng: number, lat: number): boolean => {
    const inNorthAmerica = lng >= -170 && lng <= -50 && lat >= 15 && lat <= 85;
    const inUkIreland = lng >= -12 && lng <= 2 && lat >= 49 && lat <= 61;
    return inNorthAmerica || inUkIreland;
  }
} as const;

/**
 * Check if coordinates are valid for map display.
 * Rejects:
 * - Missing coordinates (null, undefined, NaN)
 * - Coordinates near 0,0 (ocean off Africa - invalid GPS data)
 * - Coordinates with location_missing flag set
 */
export function isValidLocation(location: { lat?: number; lng?: number; location_missing?: boolean } | null | undefined): boolean {
  if (!location) return false;
  if (location.location_missing) return false;

  const { lat, lng } = location;

  // Check for valid numbers
  if (typeof lat !== 'number' || typeof lng !== 'number') return false;
  if (isNaN(lat) || isNaN(lng)) return false;

  // Reject coordinates near 0,0 (Gulf of Guinea - invalid GPS default)
  // This catches database records with uninitialized or default 0,0 coordinates
  if (Math.abs(lat) < 1 && Math.abs(lng) < 1) return false;

  return true;
}

/**
 * Calculate weighted centroid bounds for a set of coordinates.
 *
 * This solves the "outlier distortion" problem where a few units far from the main cluster
 * (e.g., 1% of units in Europe when 99% are in USA) would cause fitBounds to center on the
 * Atlantic Ocean instead of the USA.
 *
 * Algorithm:
 * 1. Calculate weighted average center (pulls toward density, not outliers)
 * 2. Use percentile-based extents (95th percentile by default) to exclude outliers
 * 3. Construct symmetric bounds centered on the weighted average
 *
 * @param coordinates Array of {lat, lng} coordinates
 * @param percentile Percentile of distances to include (0-1, default 0.95 = 95%)
 * @returns { center: [lng, lat], sw: [lng, lat], ne: [lng, lat] } or null if no valid coords
 */
export function calculateWeightedCentroidBounds(
  coordinates: Array<{ lat: number; lng: number }>,
  percentile: number = 0.95
): { center: [number, number]; sw: [number, number]; ne: [number, number] } | null {
  if (coordinates.length === 0) return null;

  // 1. Calculate weighted average center (centroid)
  let sumLat = 0;
  let sumLng = 0;

  coordinates.forEach((coord) => {
    sumLat += coord.lat;
    sumLng += coord.lng;
  });

  const avgLat = sumLat / coordinates.length;
  const avgLng = sumLng / coordinates.length;

  // 2. Calculate distances from centroid for each coordinate
  const distances = coordinates.map((coord) => ({
    diffLat: Math.abs(coord.lat - avgLat),
    diffLng: Math.abs(coord.lng - avgLng),
    // Euclidean distance for sorting (approximate, good enough for percentile)
    distance: Math.sqrt(
      Math.pow(coord.lat - avgLat, 2) + Math.pow(coord.lng - avgLng, 2)
    ),
  }));

  // 3. Sort by distance and find the percentile cutoff
  distances.sort((a, b) => a.distance - b.distance);
  const percentileIndex = Math.floor(distances.length * percentile) - 1;
  const cutoffIndex = Math.max(0, Math.min(percentileIndex, distances.length - 1));

  // 4. Use the percentile point's extents as our bounds
  // This excludes outliers beyond the percentile
  let maxDiffLat = 0;
  let maxDiffLng = 0;

  // Include all points up to the percentile
  for (let i = 0; i <= cutoffIndex; i++) {
    if (distances[i].diffLat > maxDiffLat) maxDiffLat = distances[i].diffLat;
    if (distances[i].diffLng > maxDiffLng) maxDiffLng = distances[i].diffLng;
  }

  // 5. Add a small buffer to ensure edge markers are visible (5% padding)
  const latBuffer = maxDiffLat * 0.05;
  const lngBuffer = maxDiffLng * 0.05;
  maxDiffLat += latBuffer;
  maxDiffLng += lngBuffer;

  // 6. Construct symmetric bounds centered on weighted average
  // Clamp latitude to valid range (-90 to 90)
  const swLat = Math.max(-90, avgLat - maxDiffLat);
  const neLat = Math.min(90, avgLat + maxDiffLat);

  return {
    center: [avgLng, avgLat],
    sw: [avgLng - maxDiffLng, swLat],
    ne: [avgLng + maxDiffLng, neLat],
  };
}

// Mobile padding for fitBounds
export const BOUNDS_PADDING = {
  mobile: {
    top: 60,    // Header height + padding
    bottom: 80, // Filter View button height + small margin above bottom controls
    left: 40,
    right: 40,
  },
  desktop: {
    top: 50,
    bottom: 110, // Filter View button (50px) + position (30px) + padding (30px)
    left: 360 + 50, // Sidebar width + padding
    right: 50,
  },
} as const;

// Hover timing configuration
export const HOVER_CONFIG = {
  leaveDelay: 200, // 200ms delay - short dismiss when leaving marker
  popupLeaveDelay: 300, // 300ms delay for popup mouseout
} as const;

// Popup configuration for mobile vs desktop
export const POPUP_CONFIG = {
  mobile: {
    maxWidth: 'calc(100vw - 32px)', // 16px padding each side
    collisionPadding: 16, // More padding on mobile for safe areas
    anchorThreshold: 0.4, // 40% from top triggers bottom anchor (more top placements)
    safeAreas: {
      top: 60, // Header height + padding
      bottom: 80, // Filter View button + small margin (matching BOUNDS_PADDING)
      left: 16,
      right: 16,
    },
  },
  desktop: {
    collisionPadding: 8, // Standard padding on desktop
    anchorThresholds: {
      top: 0.3, // 30% from top
      bottom: 0.7, // 70% from top
      left: 0.4, // 40% from left (compensate for sidebar)
      right: 0.6, // 60% from left
    },
    safeAreas: {
      top: 50,
      bottom: 110, // Filter View button + padding (matching BOUNDS_PADDING)
      left: 360 + 50, // Sidebar width + padding
      right: 50,
    },
  },
} as const;

// Unit icon SVG (used in layers)
export const UNIT_ICON_SVG = `
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M15 18H9" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M19 18h2a1 1 0 0 0 1-1v-3.28a1 1 0 0 0-.684-.948l-1.923-.641a1 1 0 0 1-.578-.579l-1.5-4.5A1 1 0 0 0 16.382 6H14" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="7" cy="18" r="2" stroke="white" stroke-width="2" fill="white"/>
    <circle cx="17" cy="18" r="2" stroke="white" stroke-width="2" fill="white"/>
  </svg>
`;
